import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../componentes/button/button";
import CardIndicador from "../../components/solicitacoes/Shared/CardIndicador";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { useDashboardSolicitacoes } from "../../hooks/solicitacoes/useDashboardSolicitacoes";
import { useMinhasTarefas } from "../../hooks/solicitacoes/useMinhasTarefas";
import { useContadorNaoLidas } from "../../hooks/notificacoes/useNotificacoes";
import { processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../utils/solicitacoes/statusConfig";
import { tempoDecorrido } from "../../utils/solicitacoes/formatters";
import { APLICATIVOS } from "../../config/applications";
import { useApp } from "../../context/appContext";
import LOGO from "../../assets/york.png"

const ATALHOS = [
    { label: "Iniciar Solicitação", icone: "fa fa-plus", variante: "blue", rota: "/solicitacoes/nova" },
    { label: "Central de Tarefas", icone: "fa fa-tasks", variante: "outline", rota: "/solicitacoes/central-tarefas" },
    { label: "Todas as Solicitações", icone: "fa fa-clipboard-list", variante: "outline", rota: "/solicitacoes/todas" },
    { label: "Notificações", icone: "fa fa-bell", variante: "outline", rota: "/solicitacoes/notificacoes" },
];

/** Página inicial do MyDash Solicitações — visão rápida de "o que preciso fazer agora", reaproveitando os mesmos dados do Dashboard Geral e da Central de Tarefas (sem endpoint novo). */
export default function InicioSolicitacoes() {
    const navigate = useNavigate();

    const { aplicativosPermitidos } = useApp();
    const temAcesso = aplicativosPermitidos.some(app => app.key === APLICATIVOS.MYDASH_SOLICITACOES);

    const { dashboard, loading: carregandoDashboard } = useDashboardSolicitacoes();
    const { pendentesComigo, pendentesDoMeuGrupo, loading: carregandoTarefas } = useMinhasTarefas();
    const { quantidade: naoLidas } = useContadorNaoLidas();

    useEffect(() => {
        document.title = "MyDash | Solicitações";
    }, []);

    const tarefasRecentes = pendentesComigo.slice(0, 5);

    return (
        <main className="px-4 py-6 md:py-8">
            <div className="mx-auto flex flex-col gap-6">

                <div className="flex justify-between">
                    <div>
                        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">MyDash Workflows</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Processos, aprovações, tarefas e solicitações — tudo em um só lugar.
                        </p>
                    </div>
                    <div >
                        <img src={LOGO} width={150} />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <CardIndicador
                        label="Tarefas para concluir"
                        value={pendentesComigo.length}
                        icon="fa fa-inbox"
                        iconClassName="bg-primary/10 text-primary"
                        loading={carregandoTarefas}
                        onClick={() => navigate("/solicitacoes/central-tarefas")}
                    />
                    <CardIndicador
                        label="Tarefas do meu grupo"
                        value={pendentesDoMeuGrupo.length}
                        icon="fa fa-users"
                        iconClassName="bg-orange-100 text-orange-600"
                        loading={carregandoTarefas}
                        onClick={() => navigate("/solicitacoes/central-tarefas")}
                    />
                    <CardIndicador
                        label="Em andamento"
                        value={dashboard?.emAndamento ?? 0}
                        icon="fa fa-spinner"
                        iconClassName="bg-amber-100 text-amber-700"
                        loading={carregandoDashboard}
                        onClick={() => navigate("/solicitacoes/todas")}
                    />
                    <CardIndicador
                        label="Notificações não lidas"
                        value={naoLidas}
                        icon="fa fa-bell"
                        iconClassName="bg-purple-100 text-purple-600"
                        onClick={() => navigate("/solicitacoes/notificacoes")}
                    />
                </div>

                <div className="flex flex-wrap gap-2">
                    {ATALHOS.map(a => (
                        <Button key={a.rota} variant={a.variante} label={a.label} iconLeft={a.icone} onClick={() => navigate(a.rota)} />
                    ))}
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <i className="fa fa-inbox text-primary" /> Suas tarefas pendentes
                        </h2>
                        <button
                            onClick={() => navigate("/solicitacoes/central-tarefas")}
                            className="text-xs font-medium text-primary hover:underline"
                        >
                            Ver todas
                        </button>
                    </div>

                    {carregandoTarefas ? (
                        <p className="text-sm text-muted-foreground text-center py-8">Carregando...</p>
                    ) : tarefasRecentes.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">Nenhuma tarefa pendente — tudo em dia!</p>
                    ) : (
                        <div className="divide-y divide-border">
                            {tarefasRecentes.map(t => {
                                const processoLabel = processoInfo(t.tipoProcesso)?.label || t.tipoProcesso;
                                const etapaTxt = t.etapaAtual ? (etapaLabel(t.tipoProcesso, t.etapaAtual) || humanizarEtapa(t.etapaAtual)) : "Encerrada";
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => navigate(`/solicitacoes/${t.id}`)}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors"
                                    >
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-foreground">{t.numero}</span>
                                                <span className="text-sm text-muted-foreground truncate">{processoLabel}</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5">{etapaTxt}</p>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <StatusBadge status={t.status} />
                                            <span className="text-xs text-muted-foreground">{tempoDecorrido(t.dataAtualizacao)}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
